import { useEffect, useRef, useState } from "react";

/**
 * Story 15.7's client-side half of 15-minute idle auto-logout — the UX that
 * makes the server-side guarantee (`authenticate_session`'s own idle check,
 * `app/domain/identity/authentication.py`) visible and timely, rather than
 * something the Owner only discovers the next time they click something.
 *
 * `useIdleTimer` is mounted once, in `RequireAuth.tsx` (judgement call 3),
 * so it covers every authenticated screen uniformly. It tracks real
 * activity (mouse, keyboard, touch, scroll, click), throttled so a burst of
 * raw events registers only a handful of real effects (VC-15.7-11), warns
 * before the real cutoff, and broadcasts every real activity to every other
 * tab of the same browser via `BroadcastChannel` (judgement call 4) so an
 * idle-but-open second tab is kept alive by activity in an active one
 * (VC-15.7-10).
 *
 * **Judgement call 4, `BroadcastChannel` alone, deliberately not the spec's
 * own alternately-suggested persisted-browser-storage-plus-its-change-event
 * fallback.** `tests/architecture/role-source.test.ts` (VC-2.1-17) refuses
 * *any* reference to that whole class of persisted client-side storage
 * anywhere under `frontend/src`, with no per-file exemption at all — a
 * prior review round deliberately closed a wholesale-exemption hole in that
 * guard, so this story does not reopen it for a cross-tab-sync convenience
 * the app's real environment does not need: every browser this
 * deployment's 1-5 real users actually run (a personal app, modern Chrome/
 * Safari/Firefox/Edge) has supported `BroadcastChannel` since 2022. If it
 * is ever genuinely unavailable, this degrades to no cross-tab reset —
 * never a crash — which is the identical behaviour a single open tab
 * already has.
 *
 * AD-6 compliance (VC-15.7-12): every clock read here goes through the
 * injectable `now` option, never a bare, unmockable `Date.now()` inside the
 * scheduling logic itself — the default value is the only place `Date.now`
 * appears, mirroring the backend's own injected-`Clock`-with-a-real-
 * default-implementation shape (`app/platform/clock.py`).
 *
 * **Tier A review fix round.** Two real gaps closed:
 *
 * - **The heartbeat (the blocking finding).** Client-side activity used to
 *   only ever reset the *local* warning schedule and broadcast to other
 *   tabs — it never itself reached the server. An Owner working
 *   continuously on one screen with real activity but no navigation and no
 *   other fetch/mutation for >`idleTimeoutMs` would see no warning locally
 *   (correct — genuine activity is happening), while the server's own
 *   `last_seen_at` silently aged out from under them, so their next real
 *   request landed as a bare, unexplained 401. `onHeartbeat` closes this:
 *   while genuine local activity continues, it fires periodically
 *   (`heartbeatIntervalMs`, throttled the same way ordinary activity is) so
 *   the caller can make a real, lightweight authenticated request that
 *   genuinely advances the server-side session — see `IdleSessionGuard.tsx`.
 *   The heartbeat is driven *by* already-tracked activity, never counted
 *   *as* activity itself: it is called from inside the already-throttled
 *   local-activity path, and never itself calls `applyActivityAt`/
 *   `broadcastActivity` — a heartbeat firing (or its response arriving)
 *   cannot manufacture activity that was not real.
 * - **Per-account channel scoping.** `BroadcastChannel` is scoped to the
 *   browser origin, not to a signed-in account — on a shared device with
 *   two different family members signed in as themselves in separate tabs,
 *   an unscoped channel would let one account's activity silently reset
 *   the other's idle schedule. `accountId` (the signed-in user's own id,
 *   `IdleSessionGuard.tsx`'s only caller) is mixed into the channel name so
 *   two different accounts' tabs never cross-reset each other; two tabs of
 *   the *same* account still share one channel, exactly as before.
 */

//: Real, intentional interaction — mirrors the spec's own named list.
const ACTIVITY_EVENTS = ["mousemove", "keydown", "touchstart", "scroll", "click"] as const;

//: The base channel name — `channelNameFor` below mixes in the signed-in
//: account's own id so two different accounts sharing one browser never
//: cross-reset each other's idle schedule (Tier A review fix round).
const IDLE_ACTIVITY_CHANNEL_PREFIX = "propdesk:idle-activity";

/** The real channel name a given account's tabs actually share — exported
 * so a test can construct the identical name a real caller would, without
 * hardcoding the separator. */
export function channelNameFor(accountId: string): string {
  return `${IDLE_ACTIVITY_CHANNEL_PREFIX}:${accountId}`;
}

/**
 * Wraps `fn` so it runs at most once per `intervalMs`, by real elapsed time
 * from `now()` — a first call always runs immediately (activity should
 * register right away, not wait out a cold first interval), and any call
 * inside the interval that follows is dropped, not queued or delayed.
 *
 * A small, pure, standalone utility on purpose (VC-15.7-11): the real proof
 * that throttling works is a test that drives this function directly with a
 * controlled `now`, not one that depends on jsdom's own DOM-event timing.
 */
export function throttle<Args extends unknown[]>(
  fn: (...args: Args) => void,
  intervalMs: number,
  now: () => number = () => Date.now(),
): (...args: Args) => void {
  let lastRanAt: number | undefined;
  return (...args: Args) => {
    const current = now();
    if (lastRanAt !== undefined && current - lastRanAt < intervalMs) {
      return;
    }
    lastRanAt = current;
    fn(...args);
  };
}

export interface IdleTimerHandle {
  /** Stop every timer and listener this call started. */
  stop: () => void;
  /** Treat this instant as real activity — resets every timer and
   * broadcasts to every other tab, exactly like a genuine tracked DOM
   * event. `Dialog`'s own "Stay signed in" action calls this directly. */
  registerActivity: () => void;
}

export interface StartIdleTimerOptions {
  /** Milliseconds of inactivity before the session is considered timed
   * out. */
  idleTimeoutMs: number;
  /** How long before the cutoff the warning should appear — a lead time,
   * not an absolute instant. */
  warningLeadMs: number;
  /** Called once when the warning should become visible. */
  onWarn: () => void;
  /** Called once when the full idle window elapses with the warning
   * ignored. */
  onTimeout: () => void;
  /** The signed-in account's own id — mixed into the `BroadcastChannel`
   * name so two different accounts sharing one browser never cross-reset
   * each other's idle schedule (Tier A review fix round). */
  accountId: string;
  /** Called at most once every `heartbeatIntervalMs`, and only as a direct
   * consequence of already-tracked genuine activity (ordinary throttled
   * local activity, or the explicit `registerActivity()` call) — never on
   * a bare timer of its own. The one real fix for the blocking gap: local
   * activity alone never reaches the server, so a caller wires this to a
   * real, lightweight authenticated request (`IdleSessionGuard.tsx` reuses
   * `GET /meta/session-policy`) that genuinely advances the server-side
   * session's own `last_seen_at`. Must never itself be treated as activity
   * by the caller — this module never calls it from anywhere but the
   * activity paths, and never lets its own response feed back in. */
  onHeartbeat?: (() => void) | undefined;
  /** Injectable clock (AD-6-style testability) — real wall-clock
   * milliseconds by default. */
  now?: (() => number) | undefined;
  /** How often a burst of raw DOM activity events is allowed to actually
   * register (VC-15.7-11). */
  activityThrottleMs?: number | undefined;
  /** The minimum real time between heartbeats, while activity keeps
   * providing a reason to send one. Defaults to `heartbeatIntervalMsFor`
   * so it scales down sensibly for a short, test-only `idleTimeoutMs`
   * override (VC-15.7-16) instead of a fixed production interval that
   * would never fire inside a short live-check window. */
  heartbeatIntervalMs?: number | undefined;
  /** The `window` to attach real DOM listeners to — overridable so a test
   * can supply a scoped `EventTarget` double instead of the shared jsdom
   * `window` (needed to prove two independent tab instances genuinely
   * reach each other only via the broadcast channel, not by accident
   * sharing one listener). Defaults to the real global `window`. */
  target?: EventTarget | undefined;
}

const DEFAULT_ACTIVITY_THROTTLE_MS = 5_000;
//: A floor so a pathologically small `idleTimeoutMs` can't spam the
//: server — otherwise unused in practice, since every real
//: `idleTimeoutMs` (production default or VC-15.7-16's short override)
//: comfortably clears it once divided by four.
const MIN_HEARTBEAT_INTERVAL_MS = 10_000;

//: Mirrors `_LAST_SEEN_TOUCH_THROTTLE` (`app/domain/identity/
//: authentication.py`) — the ceiling the server's own real write-throttle
//: is capped at. Named and exported here (Tier A review fix round) so
//: this function's own choice of divisor is traceable to a real backend
//: constant, not a coincidence between two independently-maintained
//: "divide by N"s, and so a test can compute the server's own real
//: write-eligible threshold (`Math.min(SERVER_WRITE_THROTTLE_CEILING_MS,
//: idleTimeoutMs / 3)` — the identical shape `_touch_throttle_for` uses)
//: and verify this function's own output clears it with real margin, at
//: any configured `idle_timeout_minutes` — not simply trust the two
//: files agree by inspection.
export const SERVER_WRITE_THROTTLE_CEILING_MS = 60_000;

/**
 * A quarter of the idle window, floored at `MIN_HEARTBEAT_INTERVAL_MS`.
 *
 * **Tier A review fix round — a real, traced bug fixed here.** The
 * previous version of this function divided by three, sized only against
 * the idle window itself, with no relationship at all to the server's own
 * real write-throttle. At the shortest `Settings` allows
 * (`idle_timeout_minutes=1`, and also VC-15.7-16's own recommended short
 * live-check override) that fixed 60-second throttle was numerically
 * *equal* to the idle window, so a heartbeat landing right at the
 * server's own write-eligible instant collided with the identical instant
 * the server revoked the session at — and revocation, checked first,
 * always won. Confirmed reproducible directly against
 * `touch_or_expire_session`: a realistic heartbeat cadence still died at
 * exactly the one-minute mark regardless of how densely it arrived
 * (`tests/api/test_idle_timeout.py`'s own permanent regression pin).
 *
 * The real fix is server-side (`_touch_throttle_for`, `app/domain/
 * identity/authentication.py`'s own doc comment has the full reasoning):
 * the server's own throttle now scales down to `idle_timeout / 3`
 * whenever that is smaller than `SERVER_WRITE_THROTTLE_CEILING_MS`. This
 * function's own share of the fix is choosing a quarter rather than a
 * third: `idleTimeoutMs / 4 < idleTimeoutMs / 3` holds for every positive
 * `idleTimeoutMs`, so a heartbeat at this cadence always lands with real
 * margin *before* the server's own write-eligible threshold opens, rather
 * than exactly astride it — true at any configured `idle_timeout_minutes`,
 * by construction, without this function needing to special-case small
 * windows against the ceiling itself (a large idle window's own quarter
 * already clears the 60-second ceiling by a wide margin on its own; a
 * small one's own quarter stays proportionally under the server's own
 * proportionally-scaled-down real threshold).
 */
export function heartbeatIntervalMsFor(idleTimeoutMs: number): number {
  return Math.max(MIN_HEARTBEAT_INTERVAL_MS, Math.floor(idleTimeoutMs / 4));
}

/**
 * Starts tracking. Framework-agnostic on purpose — `useIdleTimer` (below)
 * is the thin React lifecycle wrapper; this function is what a test drives
 * directly for the parts that do not need a real component tree.
 */
export function startIdleTimer(options: StartIdleTimerOptions): IdleTimerHandle {
  const {
    idleTimeoutMs,
    warningLeadMs,
    onWarn,
    onTimeout,
    accountId,
    onHeartbeat,
    now = () => Date.now(),
    activityThrottleMs = DEFAULT_ACTIVITY_THROTTLE_MS,
    heartbeatIntervalMs = heartbeatIntervalMsFor(idleTimeoutMs),
    target = typeof window !== "undefined" ? window : undefined,
  } = options;

  let warnHandle: ReturnType<typeof setTimeout> | undefined;
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  let lastKnownActivityAt = now();
  let stopped = false;

  function clearHandles() {
    if (warnHandle !== undefined) {
      clearTimeout(warnHandle);
      warnHandle = undefined;
    }
    if (timeoutHandle !== undefined) {
      clearTimeout(timeoutHandle);
      timeoutHandle = undefined;
    }
  }

  /** Re-arm both timers from `at` — never from "now" directly, so a signal
   * that arrived describing an *earlier* real activity instant (a stale
   * cross-tab broadcast, say) cannot rearm anything past where a more
   * recent local activity already put it (`registerActivity`'s own guard
   * below is what actually prevents that; this function trusts its
   * caller). */
  function scheduleFrom(at: number) {
    clearHandles();
    lastKnownActivityAt = at;
    if (stopped) {
      return;
    }
    const elapsed = now() - at;
    const warnInMs = Math.max(0, idleTimeoutMs - warningLeadMs - elapsed);
    const timeoutInMs = Math.max(0, idleTimeoutMs - elapsed);

    warnHandle = setTimeout(() => {
      onWarn();
    }, warnInMs);
    timeoutHandle = setTimeout(() => {
      onTimeout();
    }, timeoutInMs);
  }

  /** The one real effect of "activity happened at `at`" on this instance's
   * own local schedule — called both for genuine local activity and for a
   * cross-tab signal describing activity that happened elsewhere. */
  function applyActivityAt(at: number) {
    // A cross-tab broadcast describing activity older than what this tab
    // already knows about must never move its own deadline *earlier* —
    // only ever forward.
    if (at < lastKnownActivityAt) {
      return;
    }
    scheduleFrom(at);
  }

  function broadcastActivity(at: number) {
    try {
      channel?.postMessage(at);
    } catch {
      // No channel (unsupported/closed) is not fatal — cross-tab sync is
      // best-effort, never load-bearing for the single-tab case.
    }
  }

  //: `undefined` until the first heartbeat — the very first genuine local
  //: activity always sends one immediately (mirrors `throttle`'s own "a
  //: first call always runs right away" rule), since the server's own
  //: `last_seen_at` should be refreshed the moment real activity resumes,
  //: not after waiting out a cold first interval.
  let lastHeartbeatAt: number | undefined;

  /** The blocking-finding fix: called only from an already-tracked
   * activity path (never on a bare timer of its own), so a heartbeat is
   * always the direct consequence of real activity, never a substitute
   * for it. `force` is for the explicit `registerActivity()` call
   * ("Stay signed in") — a single, deliberate, user-initiated action, not
   * a raw-event storm, so it always sends one immediately rather than
   * waiting out `heartbeatIntervalMs`. */
  function maybeHeartbeat(at: number, { force = false } = {}) {
    if (!onHeartbeat) {
      return;
    }
    if (!force && lastHeartbeatAt !== undefined && at - lastHeartbeatAt < heartbeatIntervalMs) {
      return;
    }
    lastHeartbeatAt = at;
    onHeartbeat();
  }

  const throttledRegisterAndBroadcast = throttle(
    () => {
      const at = now();
      applyActivityAt(at);
      broadcastActivity(at);
      // Deliberately NOT triggered by a cross-tab-received broadcast
      // (`applyActivityAt` called from `channel.onmessage` below) — the
      // *other* tab that saw the real local activity is the one that
      // heartbeats; this tab doing so too would be redundant, never
      // incorrect, but every real request this deployment's 1-5 users
      // generate is one worth not duplicating for nothing.
      maybeHeartbeat(at);
    },
    activityThrottleMs,
    now,
  );

  function onLocalActivity() {
    throttledRegisterAndBroadcast();
  }

  let channel: BroadcastChannel | null = null;
  if (typeof BroadcastChannel !== "undefined") {
    channel = new BroadcastChannel(channelNameFor(accountId));
    channel.onmessage = (event: MessageEvent<number>) => {
      if (typeof event.data === "number") {
        applyActivityAt(event.data);
      }
    };
  }

  for (const eventName of ACTIVITY_EVENTS) {
    target?.addEventListener(eventName, onLocalActivity, { passive: true });
  }

  scheduleFrom(lastKnownActivityAt);

  return {
    stop() {
      stopped = true;
      clearHandles();
      for (const eventName of ACTIVITY_EVENTS) {
        target?.removeEventListener(eventName, onLocalActivity);
      }
      channel?.close();
    },
    registerActivity() {
      const at = now();
      applyActivityAt(at);
      broadcastActivity(at);
      maybeHeartbeat(at, { force: true });
    },
  };
}

export interface UseIdleTimerOptions {
  /** Whether tracking should run at all — `false` while signed out, so no
   * timer/listener exists for a visitor who has no session to idle-time-out
   * of. */
  enabled: boolean;
  idleTimeoutMs: number;
  warningLeadMs: number;
  /** Called once, on the real transition into the timed-out state — never
   * called again until the hook is re-armed by `dismissWarning`/re-mount. */
  onTimeout: () => void;
  /** The signed-in account's own id — see `startIdleTimer`'s identical
   * option for why (per-account `BroadcastChannel` scoping). `undefined`
   * while the account is not yet known; tracking does not start until
   * both `enabled` and this are real (the effect below gates on it). */
  accountId: string | undefined;
  /** See `startIdleTimer`'s identical option — the blocking-finding fix. */
  onHeartbeat?: (() => void) | undefined;
  now?: (() => number) | undefined;
  activityThrottleMs?: number | undefined;
  heartbeatIntervalMs?: number | undefined;
  target?: EventTarget | undefined;
}

export interface UseIdleTimerResult {
  /** Whether the warning should render right now. */
  warningVisible: boolean;
  /** The "Stay signed in" action, and the thing any other real interaction
   * with the warning itself should also call: genuine activity, resets the
   * window and dismisses the warning (AC: "dismissing/interacting with the
   * warning itself counts as activity"). */
  stayActive: () => void;
}

/**
 * The React lifecycle wrapper around `startIdleTimer`. Mounted once, near
 * `RequireAuth`/`AppShell` (judgement call 3) — every authenticated screen
 * is covered uniformly, and nothing per-route has to opt in.
 */
export function useIdleTimer({
  enabled,
  idleTimeoutMs,
  warningLeadMs,
  onTimeout,
  accountId,
  onHeartbeat,
  now,
  activityThrottleMs,
  heartbeatIntervalMs,
  target,
}: UseIdleTimerOptions): UseIdleTimerResult {
  const [warningVisible, setWarningVisible] = useState(false);
  const handleRef = useRef<IdleTimerHandle | null>(null);
  // Refs for the callbacks so a re-render (e.g. `onTimeout` recreated by the
  // caller every render) never tears down and restarts the real timers —
  // only `enabled`/`accountId`/the numeric config below should ever do that.
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;
  const onHeartbeatRef = useRef(onHeartbeat);
  onHeartbeatRef.current = onHeartbeat;

  useEffect(() => {
    if (!enabled || accountId === undefined) {
      setWarningVisible(false);
      return;
    }

    setWarningVisible(false);
    const handle = startIdleTimer({
      idleTimeoutMs,
      warningLeadMs,
      accountId,
      now,
      activityThrottleMs,
      heartbeatIntervalMs,
      target,
      onWarn: () => setWarningVisible(true),
      onTimeout: () => {
        setWarningVisible(false);
        onTimeoutRef.current();
      },
      onHeartbeat: onHeartbeatRef.current ? () => onHeartbeatRef.current?.() : undefined,
    });
    handleRef.current = handle;

    return () => {
      handle.stop();
      handleRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `now`/`target` are test-only overrides, stable in production
  }, [enabled, accountId, idleTimeoutMs, warningLeadMs, heartbeatIntervalMs]);

  function stayActive() {
    handleRef.current?.registerActivity();
    setWarningVisible(false);
  }

  return { warningVisible, stayActive };
}
